import React from 'react';
import type {Location} from 'react-router';
import HeaderLogoLocaleLink from './HeaderLogoLocaleLink';
import styles from './SiteFooter.module.css';

type FooterLink = {
  label: string;
  href: string;
};

type FooterColumn = {
  title: string;
  links: FooterLink[];
};

type SocialLink = {
  label: string;
  href: string;
  icon: string;
  color: string;
  darkColor?: string;
};

import {docsBasePath as basePath} from '../../site-config.mjs';

const columns = {
  'zh-CN': [
    {
      title: '资源',
      links: [
        {label: '产品公告', href: 'https://www.weex.com/zh-CN/product-launch'},
        {label: '条款与协议', href: 'https://www.weex.com/zh-CN/help/articles/4417379529241'},
        {label: '隐私说明', href: 'https://www.weex.com/zh-CN/help/articles/4417383227545'},
      ],
    },
    {
      title: '支持',
      links: [
        {label: '联系客服Bot', href: 'https://t.me/WEEX_support_smart_Bot'},
        {label: '帮助中心', href: 'https://www.weex.com/zh-CN/help'},
        {label: '费率标准', href: 'https://www.weex.com/zh-CN/support/rate'},
      ],
    },
    {
      title: 'Agent 原生',
      links: [
        {label: 'llms.txt', href: `${basePath}/zh-CN/llms.txt`},
        {label: 'llms-full.txt', href: `${basePath}/zh-CN/llms-full.txt`},
        {label: 'Agent 原生', href: `${basePath}/zh-CN/agent-native/overview`},
      ],
    },
  ],
  en: [
    {
      title: 'Resources',
      links: [
        {label: 'Announcements', href: 'https://www.weex.com/product-launch'},
        {label: 'Terms', href: 'https://www.weex.com/help/articles/4417379529241'},
        {label: 'Privacy', href: 'https://www.weex.com/help/articles/4417383227545'},
      ],
    },
    {
      title: 'Support',
      links: [
        {label: 'Support Bot', href: 'https://t.me/WEEX_support_smart_Bot'},
        {label: 'Help Center', href: 'https://www.weex.com/help'},
        {label: 'Fees', href: 'https://www.weex.com/support/rate'},
      ],
    },
    {
      title: 'Agent Native',
      links: [
        {label: 'llms.txt', href: `${basePath}/llms.txt`},
        {label: 'llms-full.txt', href: `${basePath}/llms-full.txt`},
        {label: 'Agent Native', href: `${basePath}/agent-native/overview`},
      ],
    },
  ],
} satisfies Record<'zh-CN' | 'en', FooterColumn[]>;

const socialLinks: Array<Omit<SocialLink, 'href'> & {href: string | Record<'zh-CN' | 'en', string>}> = [
  {
    label: 'Facebook',
    icon: 'facebook',
    color: '1877F2',
    href: {
      en: 'https://www.facebook.com/Weexglobal',
      'zh-CN': 'https://www.facebook.com/WEEXGlobal/',
    },
  },
  {
    label: 'CoinMarketCap',
    icon: 'coinmarketcap',
    color: '3861FB',
    href: {
      en: 'https://coinmarketcap.com/exchanges/weex',
      'zh-CN': 'https://coinmarketcap.com/zh/exchanges/weex/',
    },
  },
  {
    label: 'Discord',
    icon: 'discord',
    color: '5865F2',
    href: {
      en: 'https://discord.gg/2CFnGbMzbh',
      'zh-CN': 'https://discord.com/invite/2CFnGbMzbh',
    },
  },
  {label: 'LinkedIn', icon: 'linkedin', color: '0A66C2', href: 'https://www.linkedin.com/company/weex-global'},
  {
    label: 'X',
    icon: 'x',
    color: '111111',
    darkColor: 'ffffff',
    href: {
      en: 'https://x.com/WEEX_Official',
      'zh-CN': 'https://x.com/WeexCn',
    },
  },
  {
    label: 'Telegram',
    icon: 'telegram',
    color: '26A5E4',
    href: {
      en: 'https://t.me/+Y72JdNeHcUw3NWQ1',
      'zh-CN': 'https://t.me/+7jac6zttXxZjOTRl',
    },
  },
];

function isChinesePath(pathname: string): boolean {
  const path = pathname === basePath
    ? '/'
    : pathname.startsWith(`${basePath}/`) ? pathname.slice(basePath.length) : pathname;
  return path === '/zh-CN' || path.startsWith('/zh-CN/');
}

function SocialIcon({icon, label, color, darkColor}: Omit<SocialLink, 'href'>) {
  if (icon === 'linkedin') {
    return <span className={styles.linkedinIcon} aria-hidden="true" style={{'--icon-color': `#${color}`} as React.CSSProperties}>in</span>;
  }

  return (
    <img
      alt=""
      aria-hidden="true"
      className={styles.socialIcon}
      loading="lazy"
      src={`https://cdn.simpleicons.org/${icon}/${color}/${darkColor ?? color}`}
      title={label}
    />
  );
}

export default function SiteFooter({location}: {location: Location}): React.ReactNode {
  const zh = isChinesePath(location.pathname);
  const locale = zh ? 'zh-CN' : 'en';
  const localizedSocials = socialLinks.map((item) => ({
    ...item,
    href: typeof item.href === 'string' ? item.href : item.href[locale],
  }));

  return (
    <>
      <HeaderLogoLocaleLink />
      <div className={`${styles.footer} weex-site-footer`}>
        <nav className={styles.columns} aria-label={zh ? '页脚导航' : 'Footer navigation'}>
          {columns[locale].map((column) => (
            <section className={styles.column} key={column.title}>
              <h2>{column.title}</h2>
              <ul>
                {column.links.map((link) => (
                  <li key={link.href}>
                    <a href={link.href} target={link.href.startsWith('https://') ? '_blank' : undefined} rel={link.href.startsWith('https://') ? 'noopener noreferrer' : undefined}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
        <div className={styles.bottom}>
          <a className={styles.logoLink} href={zh ? 'https://www.weex.com/zh-CN' : 'https://www.weex.com/'} aria-label="WEEX">
            <img src={`${basePath}/img/weex-logo.svg`} alt="WEEX" />
          </a>
          <p>{zh ? '© 2026 WEEX.com. 保留所有权利。' : '© 2026 WEEX.com. All rights reserved.'}</p>
          <div className={styles.socials} aria-label={zh ? '社交媒体链接' : 'Social links'}>
            {localizedSocials.map((social) => (
              <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" aria-label={social.label} title={social.label}>
                <SocialIcon icon={social.icon} label={social.label} color={social.color} darkColor={social.darkColor} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
